const byId = (a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
/** Adds a new message or replaces the copy we have. */
export function upsertMessage(log, message) {
    const index = log.findIndex((m) => m.id === message.id);
    if (index >= 0)
        return log.map((m, i) => (i === index ? message : m));
    const last = log.at(-1);
    if (!last || last.id < message.id)
        return [...log, message];
    return [...log, message].sort(byId);
}
/** Older history goes in front; `added` is how many messages landed before the old first one. */
export function prependOlder(log, older) {
    const known = new Set(log.map((m) => m.id));
    const fresh = older.filter((m) => !known.has(m.id));
    return { messages: [...fresh, ...log], added: fresh.length };
}
/**
 * Folds the latest page into what we had, e.g. after a reconnect. Everything from the page's
 * first message on is replaced, since edits and deletes may have been missed. When the page
 * doesn't reach back to our newest message there is a gap, and the page replaces the log.
 */
export function mergeLatest(log, page) {
    const first = page.messages[0];
    const newestKnown = log.at(-1);
    if (!first || !newestKnown || (page.hasMore && newestKnown.id < first.id)) {
        return { messages: [...page.messages], reset: true, added: 0 };
    }
    const kept = log.filter((m) => m.id < first.id);
    const oldestKnown = log[0];
    const added = kept.length > 0 ? 0 : page.messages.filter((m) => m.id < oldestKnown.id).length;
    return { messages: [...kept, ...page.messages], reset: false, added };
}
/** Deleted messages stay as tombstones; replies to them show the original is gone. */
export function markDeleted(log, messageId, at) {
    return log.map((m) => {
        if (m.id === messageId)
            return { ...m, content: '', deletedAt: at, reactions: [], pinned: false };
        if (m.replyTo?.id === messageId)
            return { ...m, replyTo: { ...m.replyTo, content: '', deleted: true } };
        return m;
    });
}
export function setReaction(log, messageId, emoji, userId, on) {
    return log.map((m) => {
        if (m.id !== messageId)
            return m;
        const existing = m.reactions.find((r) => r.emoji === emoji);
        if (on) {
            if (existing?.userIds.includes(userId))
                return m;
            const reactions = existing
                ? m.reactions.map((r) => (r === existing ? { ...r, userIds: [...r.userIds, userId] } : r))
                : [...m.reactions, { emoji, userIds: [userId] }];
            return { ...m, reactions };
        }
        if (!existing?.userIds.includes(userId))
            return m;
        const reactions = m.reactions
            .map((r) => (r === existing ? { ...r, userIds: r.userIds.filter((id) => id !== userId) } : r))
            .filter((r) => r.userIds.length > 0);
        return { ...m, reactions };
    });
}
/** People named under a thread's first message ("Ayşe, Burak"), as the server sends them. */
const THREAD_PEOPLE = 3;
/** A thread's first message once `reply` came: one more, the latest time, its author first among the people. */
export function withReply(root, reply) {
    const thread = root.thread ?? { count: 0, lastReplyAt: reply.createdAt, people: [] };
    const people = reply.author ? [reply.author, ...thread.people.filter((p) => p.id !== reply.author.id)].slice(0, THREAD_PEOPLE) : thread.people;
    return { ...root, thread: { count: thread.count + 1, lastReplyAt: reply.createdAt > thread.lastReplyAt ? reply.createdAt : thread.lastReplyAt, people } };
}
/** A thread's first message once one of its replies was deleted (the people stay until the server says otherwise). */
export function withoutReply(root) {
    if (!root.thread)
        return root;
    const count = Math.max(0, root.thread.count - 1);
    return { ...root, thread: count > 0 ? { ...root.thread, count } : null };
}
/** A Jira issue changed in every message naming it; `null` (gone, or hidden from the reader) drops its card. */
export function withJiraCard(log, key, issue) {
    return log.map((m) => {
        if (!m.jira?.some((card) => card.key === key))
            return m;
        return { ...m, jira: issue ? m.jira.map((card) => (card.key === key ? issue : card)) : m.jira.filter((card) => card.key !== key) };
    });
}
//# sourceMappingURL=messages.js.map